import React, { useEffect, useState } from 'react'
import { Checkbox, Button, Toast } from '@seiue/ui'
import { Modal, Tag } from 'antd'
import styled from 'styled-components'
import { IGroupItem, IGroupList } from './types'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import { difference, intersection, stringifyURLQuery } from '@seiue/util'
import { reflectionStorage, store } from '@/store'
const { CheckableTag } = Tag

/**
 * 批量导出组件
 *
 * @param param - 参数
 * @param param.visible - 弹窗是否可见
 * @param param.groupItem - Tag配置项
 * @param param.downloadFile - 文件下载方法
 * @param param.onCancel - 关闭弹窗方法
 * @param param.defaultExportKeys - 默认选中的Tag keys
 * @param param.statusFixedKeys - 状态不可修改Tag keys
 * @returns React.ReactNode
 */
export const MultiExport: React.FC<{
  visible: boolean
  groupItem: IGroupItem[]
  downloadFile: (query: string, keys: string) => void
  onCancel?: () => void
  defaultExportKeys?: string[]
  statusFixedKeys?: string[]
}> = ({
  visible,
  groupItem,
  onCancel,
  downloadFile,
  defaultExportKeys,
  statusFixedKeys,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [checkedAllKeys, setCheckedAllKeys] = useState<string[]>([])

  useEffect(() => {
    if (visible) {
      defaultExportKeys && setSelectedKeys(defaultExportKeys)
      initSelectAll()
    } else {
      setSelectedKeys([])
      setCheckedAllKeys([])
    }
  }, [visible])

  const initSelectAll = () => {
    let flag = false
    groupItem.map((group) => {
      const keys = getAllGroupKeys(group.groupList)
      const diffArr = difference(keys, defaultExportKeys || [])
      if (diffArr.length === 0) {
        setCheckedAllKeys([...checkedAllKeys, group.groupLabel])
        flag = true
      }
    })
    if (!flag) {
      setCheckedAllKeys([])
    }
  }

  const getAllGroupKeys = (list: IGroupList[]) => list.map((item) => item.key)

  const handleChange = (key: string, checked: boolean, group: IGroupItem) => {
    if (statusFixedKeys?.includes(key)) {
      Toast.warning('当前字段为必选字段，无法取消选中')
      return
    }
    const { groupLabel } = group
    const allKeys = getAllGroupKeys(group.groupList)
    const nextSelectedKeys = checked
      ? [...selectedKeys, key]
      : selectedKeys.filter((t) => t !== key)
    let flag = true
    allKeys.forEach((key) => {
      if (!nextSelectedKeys.includes(key)) {
        flag = false
      }
    })
    const newCheckedAllKeys = flag
      ? [...checkedAllKeys, groupLabel]
      : checkedAllKeys.filter((k) => k !== groupLabel)
    setSelectedKeys(nextSelectedKeys)
    setCheckedAllKeys(newCheckedAllKeys)
  }

  const handleMultiChange = (e: CheckboxChangeEvent, group: IGroupItem) => {
    const keys = getAllGroupKeys(group.groupList)
    const checked = e.target.checked
    let newKeys: string[] = [],
      newGroupKeys: string[] = []
    if (checked) {
      newKeys = Array.from(new Set([...selectedKeys, ...keys]))
      newGroupKeys = [...checkedAllKeys, group.groupLabel]
    } else {
      if (statusFixedKeys) {
        const len = intersection(statusFixedKeys, keys).length
        newKeys = selectedKeys.filter(
          (k) => !keys.includes(k) || statusFixedKeys.includes(k)
        )
        if (len !== keys.length) {
          newGroupKeys = checkedAllKeys.filter((k) => k !== group.groupLabel)
        } else {
          newGroupKeys = checkedAllKeys
        }
      } else {
        newKeys = selectedKeys.filter((k) => !keys.includes(k))
        newGroupKeys = checkedAllKeys.filter((k) => k !== group.groupLabel)
      }
    }
    setSelectedKeys(newKeys)
    setCheckedAllKeys(newGroupKeys)
  }

  const isSemiSelected = (groupItem: IGroupItem) => {
    let flag = false
    groupItem.groupList.map((item: IGroupList) => {
      if (selectedKeys.includes(item.key)) {
        flag = true
      }
    })
    return flag && !checkedAllKeys.includes(groupItem.groupLabel)
  }

  const exportFile = async () => {
    const keys = selectedKeys.join(',')
    const accessToken = store.get(reflectionStorage)?.token || ''
    const query = stringifyURLQuery({
      _authorization: `${accessToken}`,
    })
    await downloadFile(query, keys)
    onCancel && onCancel()
  }

  const renderGroupList = () => {
    return groupItem
      .filter((item) => item.groupList.length > 0)
      .map((item: IGroupItem) => {
        const topWrapper = (
          <TopWrapper key={item.groupLabel}>
            <div>{item.groupLabel}</div>
            <Checkbox
              onChange={(e) => handleMultiChange(e, item)}
              checked={checkedAllKeys.includes(item.groupLabel)}
              indeterminate={isSemiSelected(item)}
            >
              全选
            </Checkbox>
          </TopWrapper>
        )
        const list = item.groupList.map((i: IGroupList) => {
          return (
            <CheckableTag
              checked={
                statusFixedKeys?.includes(i.key) ||
                selectedKeys.indexOf(i.key) > -1
              }
              onChange={(checked) => handleChange(i.key, checked, item)}
              key={i.key}
            >
              {i.label}
            </CheckableTag>
          )
        })
        return (
          <div key={item.groupLabel}>
            {topWrapper}
            <ListWrapper>{list}</ListWrapper>
          </div>
        )
      })
  }

  return (
    <Modal
      width={640}
      title="批量导出"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button
          key="cancel"
          onClick={onCancel}
        >
          取消
        </Button>,
        <Button
          key="export"
          type="primary"
          onClick={exportFile}
        >
          导出
        </Button>,
      ]}
    >
      <TopTip>请选择导出的表格中需要的字段</TopTip>
      {renderGroupList()}
    </Modal>
  )
}

const TopTip = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.text._3};
`

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 0 12px 0;
  color: ${(props) => props.theme.text._1};
  font-weight: 500;
`

const ListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  .ant-tag-checkable {
    height: 28px;
    line-height: 26px;
    font-size: 14px;
    border-radius: 6px;
    padding: 0 8px;
    background-color: ${(props) => props.theme.brand._5};
    margin: 0 0;
  }
  .ant-tag-checkable-checked {
    background-color: ${(props) => props.theme.brand._3};
  }
`
